import { HttpStatusCode } from 'axios';
import { DeepKeys } from '@tanstack/react-table';
import { MIME_TYPES } from '@mantine/dropzone';
import { Product } from '@/api/ProductApi';
import LANGUAGES from '@/configs/languages';

export enum ErrorCode {
	GENERIC = 'genericError',
	USER_NOT_FOUND = 'userNotFound',
	USER_SUSPENDED = 'userSuspended',
	USER_ALREADY_EXISTS = 'userAlreadyExists',
	DUPLICATED_EMAIL = 'duplicatedEmail',
	MISSING_FIELD = 'missingField',
	EMAIL_NOT_FOUND = 'emailNotFound',
	INVALID_EMAIL = 'emailIsNotValid',
	INVALID_PASSWORD = 'invalidPassword',
	INVALID_PHONE_NUMBER = 'phoneNumberIsNotValid',
	PASSWORD_TOO_WEAK = 'passwordTooWeak',
	DIFFERENT_PASSWORDS = 'differentPasswords',
	DIFFERENT_EMAILS = 'differentEmails',
	INVALID_REFRESH_TOKEN = 'invalidRefreshToken',
	REFRESH_TOKEN_EXPIRED = 'refreshTokenExpired',
	REFRESH_TOKEN_MALFORMED = 'refreshTokenMalformed',
	INVALID_TOKEN = 'invalidToken',
	INVALID_HASH = 'invalidHash',
	INVALID_POSTAL_CODE = 'invalidPostalCode',
	TOO_MANY_REQUESTS = 'tooManyRequests',
	INVALID_EMAIL_DOMAIN = 'invalidEmailDomain',
	REGISTRATION_FAILED = 'registrationFailed',
	NOT_FOUND = 'notFound',
}

export enum CreatorEntityType {
	ORDER = 'order',
	OFFER = 'offer',
}

export interface BaseEntity {
	id: UUID;
	createdAt: string;
	updatedAt: string;
}

export type Language = keyof typeof LANGUAGES;
export type ForeignLanguage = Exclude<Language, 'PL'>;
export type Translations<TData> = Record<ForeignLanguage, TData>;

export type SortOrder = 'ASC' | 'DESC';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export interface ApiQueryOptions<TData extends BaseEntity = any> {
	page: number;
	limit: number;
	search?: string;
	sortBy: Sort<TData>;
}

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export type CustomFilters = Record<string, unknown>;

export interface Attachment extends BaseEntity {
	path: string;
	name: string;
	size: number;
	mimetype: AppMimeType;
}

export type UUID = ReturnType<typeof crypto.randomUUID>;

export type ApiErrorMessage = ErrorCode | string | (ErrorCode | string)[];

export interface ApiError {
	error: string;
	message: ApiErrorMessage;
	status: HttpStatusCode;
}

export interface AppError {
	status: HttpStatusCode;
	stack: (ErrorCode | string)[];
}

export enum MouseButtons {
	LEFT,
	MIDDLE,
	RIGHT,
}

export enum Currency {
	PLN = 'PLN',
	EUR = 'EUR',
}

export enum Province {
	DOLNOSLASKIE = 'Województwo dolnośląskie',
	KUJAWSKO_POMORSKIE = 'Województwo kujawsko-pomorskie',
	LUBELSKIE = 'Województwo lubelskie',
	LUBUSKIE = 'Województwo lubuskie',
	LODZKIE = 'Województwo łódzkie',
	MALOPOLSKIE = 'Województwo małopolskie',
	MAZOWIECKIE = 'Województwo mazowieckie',
	OPOLSKIE = 'Województwo opolskie',
	PODKARPACKIE = 'Województwo podkarpackie',
	PODLASKIE = 'Województwo podlaskie',
	POMORSKIE = 'Województwo pomorskie',
	SWIETOKRZYSKIE = 'Województwo świętokrzyskie',
	WARMINSKO_MAZURSKIE = 'Województwo warmińsko-mazurskie',
	WIELKOPOLSKIE = 'Województwo wielkopolskie',
	SLASKIE = 'Województwo śląskie',
	ZACHODNIOPOMORSKIE = 'Województwo zachodniopomorskie',
}

export type AppMimeType = (typeof MIME_TYPES)[keyof typeof MIME_TYPES];

export enum ComponentRelationType {
	ATTRIBUTE_VALUE = 'attributeValue',
	PRODUCT_PROPERTY = 'productProperty',
	ATTRIBUTE = 'attribute',
}

export enum ComponentRelationAction {
	SHOW = 'show',
	HIDE = 'hide',
}

export enum RelationConditionOperator {
	LTE = 'lte',
	GTE = 'gte',
	EQ = 'eq',
}

export enum ComponentRelationOperator {
	OR = 'or',
	AND = 'and',
}

export interface ComponentRelation {
	action: ComponentRelationAction;
	operator: ComponentRelationOperator;
	conditions: ComponentRelationCondition[];
}

export interface ComponentRelationCondition {
	type: ComponentRelationType;
	objectId: UUID;
	property: keyof Pick<Product, 'surface' | 'weight'>;
	propertyValue: string;
	propertyOperator: RelationConditionOperator;
}

export interface PriceRule {
	amount: number;
	type: 'percentage' | 'fixed';
}

export enum Dimension {
	WIDTH = 'width',
	HEIGHT = 'height',
}
